import type { CSSProperties } from 'react';
import styled, { css } from 'styled-components';
import {
  AllieTableCellAlign,
  AllieTableCellTone,
  AllieTableDensity,
  AllieTableInteraction,
  AllieTableLayout,
  AllieTableRowStyle,
  AllieTableVariant,
} from './AllieTable.types';

const getCellPadding = (density: AllieTableDensity) => {
  if (density === 'compact') return '0.5rem 0.75rem';
  if (density === 'comfortable') return '1rem 1.25rem';
  return '0.75rem 1rem';
};

const getHeadPadding = (density: AllieTableDensity) => {
  if (density === 'compact') return '6px 12px';
  if (density === 'comfortable') return '10px 20px';
  return '12px 16px';
};

const getTextAlign = (align: AllieTableCellAlign) => {
  if (align === 'center') return 'center';
  if (align === 'right') return 'right';
  return 'left';
};

export const StyledTableContainer = styled.div<{ $variant: AllieTableVariant }>`
  width: 100%;
  background: ${({ $variant }) =>
    $variant === 'card' ? 'var(--allie-color-white)' : 'var(--allie-color-transparent)'};
  border: ${({ $variant }) =>
    $variant === 'card' ? '1px solid var(--allie-color-neutral-20)' : 'none'};
  border-radius: ${({ $variant }) => ($variant === 'card' ? '12px' : '0')};
  overflow: hidden;
`;

export const StyledTableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  scrollbar-color: var(--allie-color-neutral-40) var(--allie-color-neutral-10);
`;

export const StyledTable = styled.table.attrs({
  'data-allie-table': '',
})<{
  $layout: AllieTableLayout;
  $minWidth?: CSSProperties['minWidth'];
}>`
  width: 100%;
  min-width: ${({ $minWidth }) => $minWidth || '100%'};
  table-layout: ${({ $layout }) => $layout};
  border-collapse: collapse;
  border-spacing: 0;
`;

export const StyledTableCaption = styled.caption`
  padding: 0.875rem 1rem;
  color: var(--allie-color-neutral-50);
  font-size: 0.875rem;
  line-height: 1.4;
  text-align: left;
`;

export const StyledTableHeader = styled.thead`
  background: var(--allie-color-neutral-5);
`;

export const StyledTableBody = styled.tbody``;

export const StyledTableFooter = styled.tfoot`
  border-top: 1px solid var(--allie-color-neutral-20);
  background: var(--allie-color-neutral-10);
`;

export const StyledTableRow = styled.tr<{
  $rowStyle: AllieTableRowStyle;
  $interaction: AllieTableInteraction;
}>`
  border-bottom: 1px solid var(--allie-color-neutral-20);

  ${({ $rowStyle }) =>
    $rowStyle === 'zebra' &&
    css`
      &:nth-child(even) {
        background: var(--allie-color-neutral-10);
      }
    `}

  ${({ $interaction }) =>
    $interaction === 'hover' &&
    css`
      &:hover {
        background: var(--allie-color-neutral-20);
      }
    `}

  &:last-child {
    border-bottom: none;
  }
`;

export const StyledTableHeadCell = styled.th<{
  $align: AllieTableCellAlign;
  $density: AllieTableDensity;
  $width?: CSSProperties['width'];
}>`
  width: ${({ $width }) => $width || 'auto'};
  padding: ${({ $density }) => getHeadPadding($density)};
  text-align: ${({ $align }) => getTextAlign($align)};
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--allie-color-neutral-50);
  white-space: nowrap;
  vertical-align: middle;
  border-bottom: 1px solid var(--allie-color-neutral-20);
`;

export const StyledTableCell = styled.td<{
  $align: AllieTableCellAlign;
  $density: AllieTableDensity;
  $tone: AllieTableCellTone;
  $width?: CSSProperties['width'];
}>`
  width: ${({ $width }) => $width || 'auto'};
  padding: ${({ $density }) => getCellPadding($density)};
  color: ${({ $tone }) =>
    $tone === 'muted' ? 'var(--allie-color-neutral-50)' : 'var(--allie-color-neutral-main)'};
  font-size: ${({ $tone }) => ($tone === 'muted' ? '0.75rem' : '0.875rem')};
  font-weight: 400;
  line-height: 1.4;
  text-align: ${({ $align }) => getTextAlign($align)};
  vertical-align: middle;

  ${({ $align }) =>
    $align === 'right' &&
    css`
      font-variant-numeric: tabular-nums;
    `}
`;

export const StyledTableEmptyCell = styled(StyledTableCell)`
  && {
    padding: 2rem 1rem;
    color: var(--allie-color-neutral-40);
    text-align: center;
  }
`;

export const TableStatusBadge = styled.span<{ $status: 'ok' | 'warning' | 'error' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: var(--allie-radius-full, 9999px);
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  background: ${({ $status }) => {
    if ($status === 'ok') return 'rgba(6, 118, 71, 0.1)';
    if ($status === 'warning') return 'rgba(247, 144, 9, 0.1)';
    return 'rgba(180, 35, 24, 0.1)';
  }};
  color: ${({ $status }) => {
    if ($status === 'ok') return 'var(--allie-color-success-main)';
    if ($status === 'warning') return 'var(--allie-color-warning-main)';
    return 'var(--allie-color-error-main)';
  }};
`;

export const TableProgress = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.375rem;
  width: 100%;
  min-width: 0;
`;

export const TableProgressTrack = styled.div`
  flex: 0 0 40px;
  width: 40px;
  height: 6px;
  background: var(--allie-color-neutral-20);
  border-radius: var(--allie-radius-full, 9999px);
  overflow: hidden;
`;

export const TableProgressLabel = styled.span`
  flex: 0 0 auto;
  min-width: 5ch;
  text-align: left;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

export const TableProgressFill = styled.div<{
  $pct: number;
  $status: 'ok' | 'warning' | 'error';
}>`
  height: 100%;
  width: ${({ $pct }) => `${$pct}%`};
  border-radius: var(--allie-radius-full, 9999px);
  background: ${({ $status }) => {
    if ($status === 'ok') return 'var(--allie-color-success-main)';
    if ($status === 'warning') return 'var(--allie-color-warning-main)';
    return 'var(--allie-color-error-main)';
  }};
`;
